import { concat, flatten, uniq } from 'lodash';
import {
  BufferAttribute,
  BufferGeometry,
  DoubleSide,
  Mesh,
  ShapeUtils,
  Vector2,
  Vector3,
} from 'three';
import { Point3D } from '../../../../../team-apis/otterdam/answers/shape-typings';
import { H3BasicMaterial } from '../../../../materials/basic/h3-basic-material';
import { interpolateLocation } from '../../../../utils/point-cloud-interpolation-helper';
import { pointToVector } from '../../../../utils/point-cloud-utils';
import { RendererLayer, setRendererLayers } from '../../../render-utils';
import { ConfigureMeshesConfiguration } from '../../abstract-mesh-helper';
import { buildStripedTexture } from '../../mesh-utils';
import { AbstractPolyShapeHelper } from '../abstract-polyshape-helper';

const defaultStrippedTexture = buildStripedTexture(3);

export class PolygonHelper extends AbstractPolyShapeHelper {
  protected lineWidth = 2;
  private polygonFill: Mesh<BufferGeometry, H3BasicMaterial>;

  constructor(index: number) {
    super(index, 'polygons');

    this.polygonFill = new Mesh(new BufferGeometry());
    this.polygonFill.userData = {
      shapeIndex: this.index,
      meshType: this.getPolyShapeType(),
    };

    this.thickLine.add(this.polygonFill);
    setRendererLayers([RendererLayer.Main], this.polygonFill);
  }

  override update(points: Point3D[], addInBetweenPoints: boolean) {
    super.update(concat(points, [points[0]]), addInBetweenPoints);
    this.updateFill();
  }

  override dispose() {
    super.dispose();
    this.polygonFill.geometry.dispose();
  }

  getPolyShapeType() {
    return 'polygon';
  }

  getPolyShapeName() {
    return `polygon-${this.index}`;
  }

  protected override configureMeshes(config: ConfigureMeshesConfiguration) {
    const { materialService, state } = config;
    super.configureMeshes(config);
    const shapeState = this.getShapeState(state);
    const shapeColor = this.getShapeColor(state);

    const isSolid =
      interpolateLocation(shapeState.key_locations, state.render.frameNumber)
        .visibility === 1;

    this.polygonFill.material = materialService.get(H3BasicMaterial, {
      color: shapeColor,
      map: isSolid ? null : defaultStrippedTexture,
      opacity: state.render.layerSettings.opacity / 100,
      side: DoubleSide,
      transparent: true,
      userData: { materialType: isSolid ? 'solid' : 'stripped' },
    });
  }

  protected override addPoints(points: Point3D[]) {
    super.addPoints(uniq(points));
  }

  private updateFill() {
    const points2D = this.polylinePositions.map((p) => new Vector2(p[0], p[1]));
    const triangles = ShapeUtils.triangulateShape(points2D, []);
    const trianglePoints = flatten(triangles);
    const orderedPoints = trianglePoints.map((pMap) =>
      pointToVector(this.polylinePositions[pMap]),
    );
    this.polygonFill.geometry.setFromPoints(orderedPoints);

    const planeNormal = new Vector3()
      .fromArray(this.polylinePositions[0])
      .cross(new Vector3().fromArray(this.polylinePositions[1]))
      .normalize();

    const uvs = new Float32Array(
      flatten(
        // Adapted from https://gamedev.stackexchange.com/a/172357
        // Getting the UV points for a plane
        orderedPoints.map((v) => {
          const crossVector = v.equals(new Vector3(0, 0, 0))
            ? new Vector3(0, 0, 1)
            : new Vector3(1, 0, 0);
          const v1 = planeNormal.clone().cross(crossVector).normalize();
          const v2 = planeNormal.clone().cross(v1).normalize();
          return [v1.dot(v), v2.dot(v)];
        }),
      ),
    );

    // BufferGeometry does not have a built-in UV mapping, so we need to
    // pass one manually to correctly display textures like the stripped one.
    this.polygonFill.geometry.setAttribute('uv', new BufferAttribute(uvs, 2));
    this.polygonFill.geometry.computeBoundingSphere();
  }
}
